import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SelectItem } from '@mydocma/dynamic-form/lib/fields/filter-select/select-item';
import { MmViewService } from '../../mm-view.service';
import { MmDbService } from '../../mm-db/mm-db-service';
import { MmProjectDbCollections } from '../../mm-db/mm-project-db-schema';
import { Company } from '@docma-mm-core/company/company';
import { Craft } from '@docma-mm-core/craft/craft';
import { Order } from '@docma-mm-core/order/order';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY_FILTER_ID } from './field-mapping.enum';
import { getCompanyInfo } from '@docma-mm-core/company/company';
import { getOrderInfo } from '@docma-mm-core/order/order';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class FieldsCompanyCraftOrderService implements OnDestroy {
  
  private currentProjectId: number;
  private companiesSubject = new BehaviorSubject<SelectItem[]>([]);
  private craftsSubject = new BehaviorSubject<SelectItem[]>([]);
  private ordersSubject = new BehaviorSubject<SelectItem[]>([]);
  private destroy$ = new Subject<void>();
  
  public companies$ = this.companiesSubject.asObservable();
  public crafts$ = this.craftsSubject.asObservable();
  public orders$ = this.ordersSubject.asObservable();
  
  constructor(
    private readonly mmViewService: MmViewService,
    private readonly mmDbService: MmDbService,
    private readonly translateService: TranslateService,
    private readonly logger: Logger
  ) {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initialize(): void {
    this.mmViewService.currentProject$
      .pipe(
        filter(project => !!project),
        takeUntil(this.destroy$)
      )
      .subscribe(project => {
        this.currentProjectId = project.Id;
        this.loadProjectData(project.MyDocmaUid);
      });
  }

  private async loadProjectData(projectMyDocmaUid: string): Promise<void> {
    try {
      const db = await this.mmDbService.getProjectDb(projectMyDocmaUid);
      
      await Promise.all([
        this.loadCompanies(db),
        this.loadCrafts(db),
        this.loadOrders(db)
      ]);
    } catch (error) {
      this.logger.error(error, '', 'FieldsCompanyCraftOrderService');
    }
  }

  private async loadCompanies(db: any): Promise<void> {
    try {
      const companies = await db.getAll(MmProjectDbCollections.companies as never) as Company[];
      
      const selectItems = this.formatCompaniesForDropdown(companies || []);
      this.companiesSubject.next(selectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsCompanyCraftOrderService');
      this.companiesSubject.next([]);
    }
  }

  private async loadCrafts(db: any): Promise<void> {
    try {
      const crafts = await db.getAll(MmProjectDbCollections.crafts as never) as Craft[];
      const selectItems = this.formatCraftsForDropdown(crafts || []);
      this.craftsSubject.next(selectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsCompanyCraftOrderService');
      this.craftsSubject.next([]);
    }
  }

  private async loadOrders(db: any): Promise<void> {
    try {
      const orders = await db.getAll(MmProjectDbCollections.orders as never) as Order[];
      const selectItems = this.formatOrdersForDropdown(orders || []);
      this.ordersSubject.next(selectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsCompanyCraftOrderService');
      this.ordersSubject.next([]);
    }
  }

  private formatCompaniesForDropdown(companies: Company[]): SelectItem[] {
    const selectItems: SelectItem[] = [];
    
    selectItems.push({
      id: EMPTY_FILTER_ID,
      text: this.translateService.instant('MM.fields.filter.empty')
    });
    
    const sortedCompanies = companies
      .filter(company => company.Id && company.IsActive !== false)
      .sort((a, b) => (a.Name1 || '').localeCompare(b.Name1 || ''));
    
    sortedCompanies.forEach(company => {
      const shortName = company.ShortName || '';
      const name1 = company.Name1 || '';
      let displayTextBase: string = '';

      if (shortName && name1) {
        displayTextBase = `${shortName} - ${name1}`;
      } else {
        displayTextBase = shortName || name1 || '';
      }

      const displayText = displayTextBase + getCompanyInfo(company, this.translateService);

      selectItems.push({
        id: company.Id.toString(),
        text: displayText
      });
    });
    
    return selectItems;
  }

  private formatCraftsForDropdown(crafts: Craft[]): SelectItem[] {
    const selectItems: SelectItem[] = [];
    
    selectItems.push({
      id: EMPTY_FILTER_ID,
      text: this.translateService.instant('MM.fields.filter.empty')
    });
    
    const sortedCrafts = crafts
      .filter(craft => craft.Id)
      .sort((a, b) => (a.Name1 || '').localeCompare(b.Name1 || ''));
    
    sortedCrafts.forEach(craft => {
      const name1 = craft.Name1 || '';
      const name2 = craft.Name2 || '';
      let displayText = '';

      if (name1 && name2) {
        displayText = `${name1} - ${name2}`;
      } else {
        displayText = name1 || name2 || '';
      }
      
      selectItems.push({
        id: craft.Id.toString(),
        text: displayText
      });
    });
    
    return selectItems;
  }

  private formatOrdersForDropdown(orders: Order[]): SelectItem[] {
    const selectItems: SelectItem[] = [];
    
    selectItems.push({
      id: EMPTY_FILTER_ID,
      text: this.translateService.instant('MM.fields.filter.empty')
    });
    
    const sortedOrders = orders
      .filter(order => order.Id && order.IsActive !== false)
      .sort((a, b) => (a.Description1 || '').localeCompare(b.Description1 || ''));
    
    sortedOrders.forEach(order => {
      const description1 = order.Description1 || '';
      const description2 = order.Description2 || '';
      let displayTextBase: string = '';

      if (description1 && description2) {
        displayTextBase = `${description1} - ${description2}`;
      } else {
        displayTextBase = description1 || description2 || '';
      }

      const displayText = displayTextBase + getOrderInfo(order, order.Company, this.translateService);
      
      selectItems.push({
        id: order.Id.toString(),
        text: displayText
      });
    });
    
    return selectItems;
  }

  public getCompanyDisplayNameById(companyId: string): string {
    const companies = this.companiesSubject.value;
    const company = companies.find(c => c.id === companyId);
    if(!company) {
      this.logger.error('[getCompanyDisplayNameById] No company found for id: ' + companyId, '', 'FieldsCompanyCraftOrderService');
      throw new Error('[getCompanyDisplayNameById] No company found for id: ' + companyId);
    }
    return company.text;
  }

  public getCraftDisplayNameById(craftId: string): string {
    const crafts = this.craftsSubject.value;
    const craft = crafts.find(c => c.id === craftId);
    if(!craft) {
      this.logger.error('[getCraftDisplayNameById] No craft found for id: ' + craftId, '', 'FieldsCompanyCraftOrderService');
      throw new Error('[getCraftDisplayNameById] No craft found for id: ' + craftId);
    }
    return craft.text;
  }

  public getOrderDisplayNameById(orderId: string): string {
    const orders = this.ordersSubject.value;
    const order = orders.find(o => o.id === orderId);
    if(!order) {
      this.logger.error('[getOrderDisplayNameById] No order found for id: ' + orderId, '', 'FieldsCompanyCraftOrderService');
      throw new Error('[getOrderDisplayNameById] No order found for id: ' + orderId);
    }
    return order.text;
  }
}
